import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Trash2, Check, Repeat, Calendar } from 'lucide-react'
import { api } from '../services/api'
import { formatCurrency, getCurrentMonth, getMonthLabel, getPreviousMonth, getNextMonth } from '../utils/format'

const recurrenceLabels = {
  monthly: { label: 'Mensuel', short: '1x/mois', color: 'bg-blue-50 text-blue-600' },
  bimonthly: { label: '1 mois sur 2', short: '1x/2mois', color: 'bg-amber-50 text-amber-600' },
  annual: { label: 'Annuel', short: '1x/an', color: 'bg-purple-50 text-purple-600' },
}

function isDueThisMonth(recurrence, month) {
  if (recurrence === 'monthly') return true
  if (recurrence === 'bimonthly') {
    const [, m] = month.split('-').map(Number)
    return m % 2 === 0 // due on even months
  }
  if (recurrence === 'annual') {
    // Due on January each year (can be adjusted)
    const [, m] = month.split('-').map(Number)
    return m === 1
  }
  return true
}

export default function Budget() {
  const [categories, setCategories] = useState([])
  const [budgets, setBudgets] = useState([])
  const [monthlyIncome, setMonthlyIncome] = useState(0)
  const [month, setMonth] = useState(getCurrentMonth())
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [editRecurrence, setEditRecurrence] = useState('monthly')
  const [expandedParents, setExpandedParents] = useState({})

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [cats, buds, setts] = await Promise.all([
        api.getCategories(),
        api.getBudgets(month),
        api.getSettings(),
      ])
      setCategories(cats.categories || [])
      setBudgets(buds.budgets || [])
      setMonthlyIncome(parseFloat(setts?.settings?.monthly_income || 0))
      // Auto-expand all parents
      const expanded = {}
      ;(cats.categories || []).forEach(c => { if (c.children?.length) expanded[c.id] = true })
      setExpandedParents(expanded)
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [month])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ev.entity === 'budget' || ev.entity === 'payment' || ev.entity === 'expense')) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  const getBudgetForCategory = (catId) => {
    const b = budgets.find(b => b.category_id === catId)
    return b ? b : null
  }

  const handleSave = async (catId) => {
    const amount = parseFloat(editValue) || 0
    try {
      await api.setBudget({ category_id: catId, amount, month, recurrence: editRecurrence })
      fetchData()
      setEditing(null)
      setEditValue('')
    } catch (err) {
      alert(err.message)
    }
  }

  const handleDelete = async (budgetId) => {
    if (!confirm('Supprimer ce budget ?')) return
    try {
      await api.deleteBudget(budgetId)
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const toggleParent = (parentId) => {
    setExpandedParents(prev => ({ ...prev, [parentId]: !prev[parentId] }))
  }

  const getParentTotal = (parent) => {
    if (!parent.children) return 0
    return parent.children.reduce((sum, child) => {
      const b = getBudgetForCategory(child.id)
      return sum + (b ? b.amount : 0)
    }, 0)
  }

  const totalBudget = budgets.reduce((sum, b) => sum + b.amount, 0)
  const epargne = monthlyIncome > 0 ? monthlyIncome - totalBudget : null

  // Catégorie "Épargne Mensuelle" pour l'allocation automatique
  const epargneCategory = categories.flatMap(p => p.children || []).find(c => c.name === 'Épargne Mensuelle')
  const epargneBudget = epargneCategory ? getBudgetForCategory(epargneCategory.id) : null
  const canAllocateSavings = epargneCategory && epargne > 0 && !epargneBudget

  const allocateToSavings = async () => {
    if (!epargneCategory || epargne <= 0) return
    try {
      await api.setBudget({ category_id: epargneCategory.id, amount: epargne, month, recurrence: 'monthly' })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => setMonth(getPreviousMonth(month))} className="p-2 rounded-lg active:bg-slate-100">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-semibold capitalize">Budget - {getMonthLabel(month)}</h2>
        <button onClick={() => setMonth(getNextMonth(month))} disabled={month >= getCurrentMonth()} className="p-2 rounded-lg active:bg-slate-100 disabled:opacity-30">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Flux budgétaire : salaire → charges → épargne */}
      {monthlyIncome > 0 ? (
        <div className="card bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Revenus du mois</span>
            <span className="text-lg font-bold text-primary-700">{formatCurrency(monthlyIncome)}</span>
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${totalBudget > monthlyIncome ? 'bg-red-500' : 'bg-primary-600'}`}
              style={{ width: `${Math.min((totalBudget / monthlyIncome) * 100, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Charges budgétées : <span className="font-semibold text-slate-700">{formatCurrency(totalBudget)}</span></span>
            <span className={`font-bold ${epargne >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              Épargne : {formatCurrency(epargne)}
            </span>
          </div>
          {canAllocateSavings && (
            <button
              onClick={allocateToSavings}
              className="mt-3 w-full py-2 rounded-xl text-sm font-medium bg-green-600 text-white active:bg-green-700 flex items-center justify-center gap-2"
            >
              <Check size={16} /> Allouer {formatCurrency(epargne)} à l'Épargne Mensuelle
            </button>
          )}
          {epargneBudget && epargne > 0 && (
            <p className="mt-2 text-xs text-green-600 text-center">✅ Épargne Mensuelle allouée : {formatCurrency(epargneBudget.amount)}</p>
          )}
        </div>
      ) : (
        <div className="card bg-primary-50 border-primary-200 text-center">
          <p className="text-sm text-slate-500">Budget total du mois</p>
          <p className="text-2xl font-bold text-primary-700">{formatCurrency(totalBudget)}</p>
          <p className="text-xs text-slate-400 mt-1">Définissez votre revenu mensuel dans les Réglages pour voir l'épargne possible</p>
        </div>
      )}

      {/* Hierarchical categories */}
      <div className="space-y-3">
        {categories.map((parent) => {
          const isExpanded = expandedParents[parent.id]
          const parentTotal = getParentTotal(parent)
          const hasChildren = parent.children && parent.children.length > 0

          return (
            <div key={parent.id} className="rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-sm">
              {/* Parent header */}
              <div
                onClick={() => hasChildren && toggleParent(parent.id)}
                className={`flex items-center gap-3 p-4 ${hasChildren ? 'cursor-pointer active:bg-slate-50' : ''}`}
              >
                <span className="text-2xl">{parent.icon}</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{parent.name}</p>
                  <p className="text-sm text-slate-500">{formatCurrency(parentTotal)}</p>
                </div>
                {hasChildren && (
                  <button className="p-1 text-slate-400">
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                )}
              </div>

              {/* Children */}
              {hasChildren && isExpanded && (
                <div className="divide-y divide-slate-50">
                  {parent.children.map((cat) => {
                    const budget = getBudgetForCategory(cat.id)
                    const isEditing = editing === cat.id
                    const due = budget ? isDueThisMonth(budget.recurrence || 'monthly', month) : true
                    const recInfo = budget ? recurrenceLabels[budget.recurrence || 'monthly'] : null

                    return (
                      <div key={cat.id} className="flex items-center gap-3 px-4 py-3 pl-8">
                        <span className="text-lg">{cat.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{cat.name}</p>
                            {recInfo && (
                              <span className={`badge ${recInfo.color} text-[10px]`}>
                                <Repeat size={10} /> {recInfo.short}
                              </span>
                            )}
                            {budget && !due && (
                              <span className="badge bg-slate-100 text-slate-400 text-[10px]">
                                <Calendar size={10} /> Non dû ce mois
                              </span>
                            )}
                            {budget && budget.payment_status === 'paid' && (
                              <span className="badge bg-green-50 text-green-600 text-[10px]">
                                <Check size={10} /> Payé
                              </span>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="flex items-center gap-2 mt-1.5">
                              <input
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="input py-1.5 text-sm flex-1"
                                placeholder="Montant"
                                autoFocus
                              />
                              <select
                                value={editRecurrence}
                                onChange={(e) => setEditRecurrence(e.target.value)}
                                className="input py-1.5 text-sm w-auto"
                              >
                                <option value="monthly">Mensuel</option>
                                <option value="bimonthly">1 mois / 2</option>
                                <option value="annual">Annuel</option>
                              </select>
                              <button onClick={() => handleSave(cat.id)} className="p-2 bg-primary-700 text-white rounded-lg shrink-0">
                                <Check size={16} />
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 mt-0.5">
                              {budget ? formatCurrency(budget.amount) : 'Non défini'}
                            </p>
                          )}
                        </div>
                        {!isEditing && (
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => {
                                setEditing(cat.id)
                                setEditValue(budget ? String(budget.amount) : '')
                                setEditRecurrence(budget?.recurrence || 'monthly')
                              }}
                              className="p-2 text-slate-400 active:text-primary-700"
                            >
                              <Plus size={18} />
                            </button>
                            {budget && (
                              <button onClick={() => handleDelete(budget.id)} className="p-2 text-slate-400 active:text-red-500">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
